import { Ref } from '@vue/composition-api'
import { fabric } from 'fabric'
import { defaultStrokeWidth, baseFontSize } from './constants'

// 获得画布尺寸、图片尺寸、缩放比例、缩放时应显示的框宽度
export function getCanvasSize(canvas: Ref<fabric.Canvas>) {
    const imageWidth = (canvas.value.backgroundImage as fabric.Image).width || 0
    const imageHeight = (canvas.value.backgroundImage as fabric.Image).height || 0
    const canvasHeight = canvas.value.getHeight()
    const canvasWidth = canvas.value.getWidth()
    const baseZoom = Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight)
    const zoom = canvas.value.getZoom()
    const cornerSize = defaultStrokeWidth * 4
    const strokeWidth = defaultStrokeWidth / zoom

    return {
        imageWidth,
        imageHeight,
        cornerSize,
        strokeWidth,
        baseZoom,
        zoom,
        canvasWidth,
        canvasHeight,
    }
}

// 处理工具栏的放大、缩小、适应画布及滚轮缩放
export function useZoom(canvas: Ref<fabric.Canvas>) {
    function onZoomHandler() {
        const { strokeWidth, cornerSize, zoom } = getCanvasSize(canvas)

        canvas.value.forEachObject(item => {
            if (item.type === 'rect') {
                item.strokeWidth = strokeWidth
                item.cornerSize = cornerSize
            } else if (item.type === 'polygon') {
                item.strokeWidth = strokeWidth
                item.cornerSize = cornerSize
                item.set({ hasControls: false })
            } else if (item.type === 'point') {
                Object.assign(item, {
                    radius: 1.5 * strokeWidth,
                    font: `${Math.ceil(baseFontSize / zoom)}px Arial`,
                })
                // item.setCoords()
            } else if (item.type === 'polygon-point') {
                const circle = item as fabric.Circle
                circle.radius = 2 * strokeWidth
            }
            item.setCoords()
        })
    }

    function zoomToFitCanvas(type?) {
        if (type) {
            const imageWidth = (canvas.value.backgroundImage as fabric.Image).width || 0
            const imageHeight = (canvas.value.backgroundImage as fabric.Image).height || 0
            const { canvasWidth, canvasHeight } = getCanvasSize(canvas)
            const zoomPoint = new fabric.Point(canvasWidth / 2, canvasHeight / 2)
            const tozoom = 1
            let left = 0
            let top = 0
            if (canvasWidth >= imageWidth && canvasHeight >= imageHeight) {
                left = -((canvasWidth - imageWidth) / 2)
                top = -((canvasHeight - imageHeight) / 2)
            }
            const origin = new fabric.Point(left, top)
            canvas.value.zoomToPoint(zoomPoint, tozoom)
            canvas.value.absolutePan(origin)
            onZoomHandler()
        } else {
            const imageWidth = (canvas.value.backgroundImage as fabric.Image).width || 0
            const imageHeight = (canvas.value.backgroundImage as fabric.Image).height || 0
            const { canvasWidth, canvasHeight, baseZoom } = getCanvasSize(canvas)
            const zoomPoint = new fabric.Point(canvasWidth / 2, canvasHeight / 2)
            const tozoom = baseZoom > 5 ? 5 : baseZoom
            const zoomImageWidth = imageWidth * tozoom
            const zoomImageHeigth = imageHeight * tozoom
            const left = -((canvasWidth - zoomImageWidth) / 2)
            const top = -((canvasHeight - zoomImageHeigth) / 2)
            const origin = new fabric.Point(left, top)

            canvas.value.zoomToPoint(zoomPoint, tozoom)
            canvas.value.absolutePan(origin)
            onZoomHandler()
        }
    }

    const zoomToPoint = zoom => {
        const { canvasHeight, canvasWidth } = getCanvasSize(canvas)
        const zoomPoint = new fabric.Point(canvasWidth / 2, canvasHeight / 2)
        canvas.value.zoomToPoint(zoomPoint, zoom)
        onZoomHandler()
    }

    function zoomUp() {
        const { zoom } = getCanvasSize(canvas)
        zoomToPoint(zoom + 0.1 > 5 ? 5 : zoom + 0.1)
    }

    function zoomDown() {
        const { zoom } = getCanvasSize(canvas)
        zoomToPoint(zoom - 0.1 < 0.1 ? 0.1 : zoom - 0.1)
    }

    function onMouseWheel(event: fabric.IEvent) {
        const unit = -(event.e as WheelEvent).deltaY / 1000
        const currentZoom = canvas.value.getZoom()
        const isMaxZoom = currentZoom === 5 && unit === 0.1
        const isMinZoom = currentZoom === 0.1 && unit === -0.1
        if (isMaxZoom || isMinZoom) return
        let zoom = Math.ceil((currentZoom + unit) * 1000) / 1000
        zoom = zoom < 0.1 ? 0.1 : zoom > 5 ? 5 : zoom
        const { x, y } = canvas.value.getPointer(event.e, true)
        canvas.value.zoomToPoint(new fabric.Point(x, y), zoom)
        onZoomHandler()
        event.e.stopPropagation()
        event.e.preventDefault()
        canvas.value.fire('mouse:wheeled', event)
    }

    return {
        zoomDown,
        zoomUp,
        zoomToFitCanvas,
        onMouseWheel,
    }
}
